/*
 * Third-party boot policy.
 *
 * The public photo gallery (`/ch/:slug`, `/ch/:slug/:gifId`) is opened by an
 * event guest on a phone, logged out, to look at their own photos. Every
 * third-party origin the shell boots there costs that guest latency and sends
 * their visit to a vendor they have no relationship with. This module is the
 * single home for "which third parties boot on which routes", so the answer
 * lives in one auditable place instead of being spread across `index.html`,
 * `src/index.js` and component effects.
 *
 * Guests get: PostHog — deferred past load+idle, anonymous
 * (`person_profiles: "identified_only"`, no autocapture, no session
 * recording), so product usage on the public gallery (`gallery_viewed`,
 * shares, downloads) is measurable without costing the guest LCP time.
 * Pre-init events are buffered in `utils/posthog` and replayed at init.
 * (Fathom served this role until movebooth.com's move to PostHog, 2026-09-28;
 * see CHANGELOG.)
 * Everyone else additionally gets: PostHog synchronously at boot (feature
 * flags are consumed early on app routes) and — via `components/Intercom`,
 * which only mounts for an authenticated user — the Intercom widget.
 *
 * Sentry is deliberately NOT here: `@sentry/react` is bundled, not a
 * script-tag third party, and it is the only thing that reports a broken
 * gallery back to us. Deferring it would blind us to exactly the errors this
 * surface is most likely to throw.
 */

import { env } from "utils/env";
import { initPostHog } from "utils/posthog";

const INTERCOM_APP_ID = "jb774wdc";
const INTERCOM_WIDGET_SRC = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`;

/*
 * Routes a logged-out event guest can land on. Mirrors — and is the single
 * source of truth for — the "blank page" chrome-less route set that
 * `containers/App` renders without the dashboard shell. Substring matching
 * (rather than exact patterns) is deliberate: it keeps the predicate
 * behaviourally identical to the check it replaced, and errs toward treating a
 * route as public, which is the safe direction (a false positive skips a
 * third party; a false negative would boot one for a guest).
 */
const PUBLIC_GUEST_PATH_SEGMENTS = ["/ch/", "/moves/", "/downloads/"];

export const isPublicGuestRoute = (pathname) =>
  typeof pathname === "string" &&
  PUBLIC_GUEST_PATH_SEGMENTS.some((segment) => pathname.includes(segment));

/*
 * Run `fn` once the page has finished loading and the main thread is idle.
 * Nothing in this module is needed to render the gallery, so none of it should
 * compete with the LCP image for bandwidth or main-thread time. `load` (not
 * `DOMContentLoaded`) matches the timing the Intercom snippet used in
 * `index.html`, so the authenticated dashboard sees the same ordering it
 * always did. The idle timeout bounds the wait on a page that never goes idle.
 */
const afterLoadWhenIdle = (fn) => {
  const schedule = () => {
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(fn, { timeout: 3000 });
    } else {
      // Safari < 16.4 has no requestIdleCallback; a macrotask is the closest
      // "not right now" available and is still after load.
      window.setTimeout(fn, 0);
    }
  };

  if (document.readyState === "complete") {
    schedule();
  } else {
    window.addEventListener("load", schedule, { once: true });
  }
};

const appendScript = (attributes) => {
  const script = document.createElement("script");
  Object.entries(attributes).forEach(([name, value]) => {
    if (name === "src") {
      script.src = value;
    } else {
      script.setAttribute(name, value);
    }
  });
  script.async = true;
  document.head.appendChild(script);
};

let intercomRequested = false;

/*
 * Intercom is one production workspace (one app id). A non-production build
 * (VITE_STAGE=staging, local dev) that booted the Messenger would create real
 * contacts for its test users in that workspace, so the Messenger runs only in
 * the production build. VITE_INTERCOM_ENABLED=true is the explicit per-build
 * opt-in for any other environment.
 */
export const isIntercomEnabled = () =>
  env.VITE_STAGE === "production" || env.VITE_INTERCOM_ENABLED === "true";

/*
 * Install the Intercom queue stub synchronously, then fetch the widget bundle
 * after load. The stub is the standard Intercom snippet's queue: calls made
 * before the bundle arrives are buffered and replayed once it does, so callers
 * can `Intercom("boot", …)` immediately.
 *
 * Called only from `components/Intercom`, which only mounts when an auth token
 * exists — that is what keeps `widget.intercom.io` and its `frame-modern.js`
 * off a logged-out guest's phone entirely. Gallery routes already hid the
 * launcher after loading it; now there is nothing to hide.
 */
export const installIntercomWidget = () => {
  if (!isIntercomEnabled()) return;
  if (intercomRequested || typeof window === "undefined") return;
  intercomRequested = true;

  if (typeof window.Intercom === "function") {
    // Already live (e.g. a second mount within the same page load).
    window.Intercom("reattach_activator");
    window.Intercom("update", window.intercomSettings);
    return;
  }

  const queue = (...args) => {
    queue.q.push(args);
  };
  queue.q = [];
  window.Intercom = queue;

  afterLoadWhenIdle(() => appendScript({ src: INTERCOM_WIDGET_SRC }));
};

/*
 * The route-keyed boot decision. Called at startup with the landing pathname
 * and again from `containers/App` on navigation, so a visitor who arrives on a
 * gallery route and later reaches an app route still gets what that route
 * needs. Every installer below is idempotent, so repeat calls are free.
 */
export const bootThirdParties = (pathname) => {
  if (isPublicGuestRoute(pathname)) {
    // Guests get PostHog — product usage on the gallery (pageviews, shares,
    // downloads) is measured there — but deferred past load+idle, so none of
    // its network cost competes with the LCP image. `utils/posthog` buffers
    // events fired before the deferred init lands. initPostHog() is
    // idempotent, so a guest who navigates into the app before idle simply
    // inits earlier via the branch below.
    afterLoadWhenIdle(initPostHog);
    return;
  }
  // App routes consume feature-flag decisions early, so init synchronously.
  initPostHog();
};

// Exported only so tests can reset module state between cases.
export const __resetForTests = () => {
  intercomRequested = false;
};
